import type { CSSProperties } from "react";
import React, { useEffect, useRef, useCallback } from "react";
import { autoUpdate, Placement, useFloating } from "@floating-ui/react-dom";
import styles from "./Popup.module.scss";
import classNames from "classnames";
import { PopupBackdropArea } from "@/uikit";

interface PopupProps {
  anchor: HTMLElement;
  placement?: Placement;
  active?: boolean;
  className?: string;
  children: React.ReactNode;
  onClose?: () => void;
  isTransparent?: boolean;
  setCloseCallback: React.Dispatch<React.SetStateAction<(() => void) | null>>;
}

export const Popup: React.FC<PopupProps> = ({
  anchor,
  placement = "bottom-start",
  active = false,
  className,
  children,
  onClose,
  isTransparent = true,
  setCloseCallback,
}) => {
  const { refs, floatingStyles, update } = useFloating({ placement });
  const popupRef = useRef<HTMLDivElement | null>(null);

  // Установка refs
  useEffect(() => {
    if (!anchor) return;
    refs.setReference(anchor);
    if (!refs.floating.current) return;

    return autoUpdate(anchor, refs.floating.current, update);
  }, [anchor, refs, update]);

  // Закрытие при клике вне
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (!popupRef.current || !anchor) return;

      const target = event.target as Node;
      if (!popupRef.current.contains(target) && !anchor.contains(target)) {
        onClose?.();
      }
    },
    [onClose, anchor],
  );

  useEffect(() => {
    if (!active) return;
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [active, handleClickOutside]);

  if (!active) return null;

  const onCloseHandle = useCallback(() => {
    if (onClose) {
      onClose();
    }

    if (document.body.classList.contains("no-scroll")) {
      document.body.classList.remove("no-scroll");
    }
  }, [JSON.stringify(onClose)]);

  useEffect(() => {
    setCloseCallback && setCloseCallback(() => onCloseHandle);
  }, [onCloseHandle, setCloseCallback]);

  useEffect(() => {
    if (!isTransparent && !document.body.classList.contains("no-scroll")) {
      document.body.classList.add("no-scroll");
    }
  }, [isTransparent]);

  return (
    <>
      <PopupBackdropArea onClick={onCloseHandle} isTransparent={isTransparent} />
      <div
        ref={(el) => {
          refs.setFloating(el);
          popupRef.current = el;
        }}
        style={floatingStyles as CSSProperties}
        className={classNames(styles.popup, className)}
      >
        {children}
      </div>
    </>
  );
};
